import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient.web";
import { apiPost } from "../lib/apiClient";
import { authCodeFor, authMessageFor } from "../lib/errorCodes";
import { isValidUsername, USERNAME_RULE_TEXT } from "../lib/usernameRules";
import BrandHeader from "../lib/BrandHeader";

const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID as string | undefined;

type Profile = {
  user_id: string;
  onboarding_state: string;
  username_hash: string | null;
};

type Status =
  | { kind: "loading" }
  | { kind: "not-logged-in" }
  | { kind: "need-verify"; email: string }
  | { kind: "need-username"; email: string }
  | { kind: "ready"; email: string }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

function isEmailVerified(user: any): boolean {
  return Boolean(user?.email_confirmed_at || user?.confirmed_at);
}

export default function ConnectExtensionPage() {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [username, setUsername] = useState("");
  const [usernameMsg, setUsernameMsg] = useState<string | null>(null);

  const hasExtensionId = useMemo(() => Boolean(EXTENSION_ID && EXTENSION_ID.length > 10), []);
  const didAutoSendRef = useRef(false);
  const didLoadGatesRef = useRef(false);

  const loginUrl = "/auth/login?returnTo=" + encodeURIComponent("/connect");

  // Listen for extension acknowledgement (optional but useful)
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== window) return;
      if (event.origin !== window.location.origin) return;

      const msg = event.data;
      if (!msg || msg.type !== "TAKENOTE_CONNECT_RESULT") return;

      if (msg.response?.ok) {
        setStatus({ kind: "sent" });
      } else {
        setStatus({
          kind: "error",
          message: msg.response?.error ?? "Extension did not accept the session.",
        });
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  async function loadGates() {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      setStatus({ kind: "error", message: authMessageFor(authCodeFor(error)) });
      return;
    }

    const session = data.session;
    if (!session?.user) {
      setStatus({ kind: "not-logged-in" });
      window.location.href = loginUrl;
      return;
    }

    const email = session.user.email ?? "unknown";

    if (!isEmailVerified(session.user)) {
      setStatus({ kind: "need-verify", email });
      // force the correct UX
      window.location.href = `/auth/verify?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent("/connect")}`;
      return;
    }

    // Username gate (required for sharing by username)
    try {
      const res = await apiPost<{ profile: Profile }>("/users/profile", session.access_token, {});
      const profile = res?.profile;

      if (!profile?.username_hash) {
        setStatus({ kind: "need-username", email });
        return;
      }
    } catch (e: any) {
      setStatus({ kind: "error", message: e?.message ?? "Failed to load profile" });
      return;
    }

    setStatus({ kind: "ready", email });
  }

  useEffect(() => {
    // StrictMode mounts effects twice in dev. Without this guard the gate
    // sequence fires two concurrent /users/profile calls that race each other.
    // Direct calls to loadGates() elsewhere (e.g. after setting a username)
    // are deliberate and stay unguarded.
    if (didLoadGatesRef.current) return;
    didLoadGatesRef.current = true;

    void loadGates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitUsername() {
    setUsernameMsg(null);

    const u = username.trim();
    if (!u) {
      setUsernameMsg("Username is required.");
      return;
    }
    // The server enforces the rule and is the authority; this tells the person first.
    if (!isValidUsername(u)) {
      setUsernameMsg(USERNAME_RULE_TEXT);
      return;
    }

    const { data, error } = await supabase.auth.getSession();
    if (error) {
      setUsernameMsg(authMessageFor(authCodeFor(error)));
      return;
    }

    const session = data.session;
    if (!session?.user) {
      setStatus({ kind: "not-logged-in" });
      window.location.href = loginUrl;
      return;
    }

    try {
      setUsernameMsg("Saving…");
      await apiPost("/users/username", session.access_token, { username: u });
      setUsernameMsg("Saved ✅");
      await loadGates();
    } catch (e: any) {
      setUsernameMsg(e?.message ?? "Failed to set username");
    }
  }

  async function sendSessionToExtension() {
    try {
      setStatus({ kind: "sending" });

      const { data, error } = await supabase.auth.getSession();
      if (error) throw new Error(authMessageFor(authCodeFor(error)));

      const session = data.session;
      if (!session?.user) {
        setStatus({ kind: "not-logged-in" });
        window.location.href = loginUrl;
        return;
      }

      if (!isEmailVerified(session.user)) {
        const email = session.user.email ?? "";
        window.location.href = `/auth/verify?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent("/connect")}`;
        return;
      }

      if (!EXTENSION_ID) {
        setStatus({
          kind: "error",
          message: "Missing VITE_EXTENSION_ID. Add it to apps/web/.env (dev) or deployment env vars.",
        });
        return;
      }

      // Post session to page; content script will forward to service worker
      window.postMessage(
        {
          type: "TAKENOTE_CONNECT",
          payload: {
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_at: session.expires_at,
            token_type: session.token_type,
            user: { id: session.user.id, email: session.user.email },
          },
        },
        window.location.origin
      );

      // If the result listener never fires (e.g., extension not installed),
      // keep UX sane by showing "sent" after posting.
      setTimeout(() => {
        setStatus((s) => (s.kind === "sending" ? { kind: "sent" } : s));
      }, 800);
    } catch (e: any) {
      setStatus({ kind: "error", message: e?.message ?? "Unknown error" });
    }
  }

  // Auto-send once when ready (no click required)
  useEffect(() => {
    if (status.kind !== "ready") return;
    if (!hasExtensionId) return;
    if (didAutoSendRef.current) return;

    didAutoSendRef.current = true;
    void sendSessionToExtension();
    // Sends once, guarded by didAutoSendRef. The function is a new one on every
    // render, so listing it would re-run this effect on every render for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, hasExtensionId]);

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-surface shadow p-6 space-y-4">
        <BrandHeader className="" />
        <h1 className="text-xl font-semibold">Connect TakeNote Extension</h1>

        {status.kind === "loading" && <div className="text-sm text-fg-soft">Loading…</div>}

        {status.kind === "not-logged-in" && (
          <div className="text-sm text-fg-body">Redirecting to login…</div>
        )}

        {status.kind === "need-verify" && (
          <div className="rounded-xl border border-warning-line bg-warning-bg p-3 text-sm text-warning">
            Please verify your email (<span className="font-mono">{status.email}</span>) before connecting.
          </div>
        )}

        {status.kind === "need-username" && (
          <>
            <div className="text-sm text-fg-body">
              Logged in as <span className="font-mono">{status.email}</span>
            </div>

            <div className="rounded-xl border border-line bg-panel p-3 text-sm text-fg-panel">
              Choose a username. This is required to send notes to other users.
            </div>

            <div className="space-y-2">
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="w-full rounded-xl border border-line-control px-3 py-2 text-sm"
                autoCapitalize="none"
                autoCorrect="off"
              />

              <button type="button" onClick={() => void submitUsername()} className="w-full rounded-xl bg-primary text-on-primary py-2">
                Save Username
              </button>

              {usernameMsg && <div className="text-xs text-fg-soft">{usernameMsg}</div>}
            </div>
          </>
        )}

        {status.kind === "ready" && (
          <>
            <div className="text-sm text-fg-body">
              Logged in as <span className="font-mono">{status.email}</span>
            </div>

            {!hasExtensionId && (
              <div className="rounded-xl border border-warning-line bg-warning-bg p-3 text-sm text-warning">
                VITE_EXTENSION_ID is not set yet. We’ll add it after we scaffold the extension.
              </div>
            )}

            <button
              type="button"
              onClick={() => void sendSessionToExtension()}
              className="w-full rounded-xl bg-primary text-on-primary py-2"
              disabled={!hasExtensionId}
            >
              Connect Extension
            </button>

            <div className="text-xs text-fg-hint">
              This grants the extension access to your TakeNote account. You can disconnect anytime.
            </div>
          </>
        )}

        {status.kind === "sending" && (
          <div className="rounded-xl border border-info-line bg-info-bg p-3 text-sm text-info">Connecting…</div>
        )}

        {/*
          Terminal on purpose. Do NOT add an action here - no "Reconnect", no
          "Check status", nothing that calls supabase.auth.getSession().

          From this point the extension holds this session's refresh token. The
          page holds the same one. getSession() refreshes an expired session
          regardless of autoRefreshToken, so a button here would spend a token
          the extension is still relying on; GoTrue treats the reuse as theft
          and revokes the whole family, signing the user out of the extension.
          See takenote_decisions.md 7.1.

          The two buttons above are safe only because they are unreachable once
          the handover has happened - before it, this page is the sole holder
          and a refresh costs nothing.
        */}
        {/*
          WHY THIS SAYS MORE THAN "you can close this tab" (11.3). Closing the
          tab was the only instruction, and it left a user with no way back in:
          the popup closed when this tab opened, and nothing said to reopen it.

          ONE SENTENCE WITH THE ICON IN IT (Phase 12.5, the product owner's
          wording). The toolbar button's own picture (toolbar-icon.svg, from
          brand/mark-toolbar.svg) is what a user is looking for, so the sentence
          shows it. The picture is decorative (alt=""): the words already say
          "the TakeNote extension".

          THE PINNING ASIDE WENT, AND THAT WAS A CHOICE. 11.3's version added
          "Can't see it? Click the puzzle-piece (Extensions) icon and pin
          TakeNote", because Chrome keeps a newly installed extension in that
          menu until it is pinned, so a brand-new user may find nothing in the
          toolbar to open. This page cannot tell a new user from a returning one
          - the keys that would say so are in the extension - so it cannot show
          the aside to new users only.
        */}
        {status.kind === "sent" && (
          <div className="rounded-xl border border-success-line bg-success-bg p-3 text-sm text-success">
            Connected ✅ Open the TakeNote{" "}
            <img
              src="/toolbar-icon.svg"
              alt=""
              width={16}
              height={16}
              className="inline-block h-4 w-4 align-[-3px]"
            />{" "}
            extension in your toolbar to continue.
          </div>
        )}

        {status.kind === "error" && (
          <div className="rounded-xl border border-danger-line bg-danger-bg p-3 text-sm text-danger">
            {status.message}
          </div>
        )}
      </div>
    </div>
  );
}
